import { Node } from 'reactflow';
import {
  DeletedOption,
  ExecutionHistory,
  Field,
  KizenFieldType,
} from './common';
import { GenericOption } from 'pages/AutomationEngine/types';

export type EmailNotificationType = 'notify_member_via_email' | 'send_email';

export type KizenStepType =
  | EmailNotificationType
  // | 'agent'
  | 'assign_team_member'
  | 'audio_transcription'
  | 'call_llm'
  | 'change_field_value'
  | 'change_tags'
  | 'code_step'
  | 'condition'
  | 'create_related_entity'
  | 'delay'
  | 'delete_scheduled_activity'
  | 'exit_loop'
  | 'file_content_extraction'
  | 'go_to_automation_step'
  | 'goal'
  | 'initialize_variable'
  | 'math_operator'
  | 'start_automation'
  | 'modify_automation'
  | 'archive_record'
  | 'search_records'
  | 'modify_related_entities'
  | 'http_request'
  | 'notify_member_via_text'
  | 'schedule_activity'
  | 'send_related_contact_email'
  | 'send_related_contact_text'
  | 'send_text'
  | 'stop_execution'
  | 'update_variable'
  | 'plugin_code_step'
  | 'branch';

export type KizenTriggerType =
  | 'activity_logged'
  | 'contact_tag_added_removed'
  | 'email_interaction'
  | 'email_link_clicked'
  | 'email_received'
  | 'field_updated'
  | 'form_submitted'
  | 'manual'
  | 'new_entity_created'
  | 'on_or_around_date'
  | 'scheduled_activity_overdue'
  | 'stage_updated'
  | 'survey_submitted'
  | 'website_visited'
  | 'interaction_occurred'
  | 'schedule'
  | 'webhook';

export type KizenGoalType =
  | 'activity_logged_goal'
  | 'scheduled_activity_overdue_goal'
  | 'field_updated_goal'
  | 'contact_tag_added_removed_goal'
  | 'on_or_around_date_goal'
  | 'email_received_goal'
  | 'email_link_clicked_goal'
  | 'form_submitted_goal'
  | 'stage_updated_goal'
  | 'survey_submitted_goal';

export type KizenInitializeVariableType =
  | 'initialize_variable_add'
  | 'initialize_variable';

export const BranchStepTypes = {
  SPLIT: 'split_branches',
  MERGE: 'merge_branches',
  BRANCH: 'branch',
  BRANCH_SPLIT: 'branch_split',
} as const;

export type KizenMergeType = ValueOf<typeof BranchStepTypes>;

export const LoopStepTypes = {
  LOOP: 'loop',
  LOOP_NEXT_OR_FINISH: 'next_or_finish',
} as const;

export type KizenLoopType = ValueOf<typeof LoopStepTypes>;

export type CommonVariableDefinitionType =
  | 'static'
  | 'logged_activity_trigger'
  | 'activity_past_due_trigger'
  | 'field_value'
  | 'variable'
  | 'trigger_time'
  | 'webhook_trigger';

export type VariableDefinitionType = Extract<
  CommonVariableDefinitionType,
  | 'static'
  | 'logged_activity_trigger'
  | 'activity_past_due_trigger'
  | 'field_value'
  | 'trigger_time'
  | 'webhook_trigger'
>;

export type VariableUpdateType = Extract<
  CommonVariableDefinitionType,
  'static' | 'field_value' | 'variable'
>;

export type KizenNodeType =
  | KizenStepType
  | KizenTriggerType
  | KizenGoalType
  | KizenInitializeVariableType
  | KizenMergeType
  | KizenLoopType
  | 'add_trigger'
  | 'unknown'
  | 'noop';

export type ConditionDetailsType =
  | 'in_group'
  | 'not_in_group'
  | 'custom_filter'
  | 'llm_decision';

export type StepDetailsType =
  | KizenStepType
  | KizenTriggerType
  | KizenInitializeVariableType
  | ConditionDetailsType
  | KizenMergeType
  | KizenLoopType;

export type Step = {
  id: string;
  type: KizenNodeType;
  actionNotifyMemberViaEmail?: any;
  actionOnFailure: string;
  description: string;
  userDescription?: string | null;
  prefix?: string;

  order: number;
  parentCondition: string;
  fromLabel?: string;
  parentStepId: string | null;
  parentKey: string | null;
  stats: any;
  stepType: EmailNotificationType | KizenInitializeVariableType;

  history?: ExecutionHistory[];
  hasError: boolean;
  isDuplicate?: boolean;
  stepError?: Record<string, any>;
  shouldSkipExecution?: boolean;
  continueWithBranch?: 'yes' | 'no';
  details?: Record<string, any> &
    Partial<Omit<Step, 'details' | 'type' | 'id'>> & {
      type?: StepDetailsType;
    };
  changeType?: 'created' | 'updated' | 'deleted';
  isBranchGroupInitiator?: boolean;
  isBranchMerged?: boolean;
  mergeParent: string | null;
  loopDepth: null | number;
  loopVariables: Array<LoopVariable>;
  // set on a Next/Finish step: the id of the loop it closes
  loopParent?: string | null;
  goalType: boolean;
  flashBackgroundColor?: string | null;
};

export type Automation = {
  id: string;
  active: boolean;
  name: string;
  apiName: string;
  steps: Step[];
  triggers: Step[];
  // partial
  numberActive: any;
  numberCompleted: any;
  numberPaused: any;
  customObject: any;
  type: any;
};

export type AutomationEmail = {
  baseMessageId: string;
  content: string;
  craftJsContent: any;
  craftJson: any;
  created: string;
  customFormName: string;
  fromNameType: string;
  htmlContent: string;
  id: string;
  sender: any;
  subject: string;
};

export type KizenNodeData = {
  id: string;
  leaf: boolean;
  branch: boolean;
  parentKey: string;
  label: object;
  hasError?: string[];
  type: KizenNodeType;
  loopDepth: number;
  loopVariables: Array<LoopVariable>;
};

export type KizenNode = Node & {
  data: KizenNodeData;
};

export type AutomationLike = {
  id: string;
  revision: number;
  triggerThrottleSeconds: number;
};

export type FieldData = Omit<Field, 'fieldType'> & {
  fieldType: KizenFieldType;
};

export type CategorizedOption = {
  label: string;
  options: (GenericOption & {
    fieldData: FieldData;
    fieldType?: KizenFieldType;
  })[];
  deleted?: boolean;
};

export type RelatedFieldCategorizedOption = {
  label: string;
  key?: string;
  options: (GenericOption & {
    fieldData: FieldData;
    fieldType?: KizenFieldType;
    emptyOnly: boolean;
    isRequired: boolean;
  })[];
};

export type ClearFieldOption = DeletedOption & {
  isMulti: boolean;
  field: Field;
  displayName: string;
};

export type ClearFieldOptions = {
  label: string;
  key: string;
  options: Array<ClearFieldOption>;
};

export type ApiCategorizedOption = {
  id: string;
  name: string;
  order: number;
  meta: object;
  fields: FieldData[];
};

export type LoopVariable = {
  loopStepId: string;
  variableName: string;
};
